@if (user$ | async; as user) {
  <button
    type="button"
    class="account-trigger"
    [class.account-trigger--open]="isOpen$ | async"
    aria-haspopup="menu"
    [attr.aria-expanded]="(isOpen$ | async) ? 'true' : 'false'"
    (click)="onToggle()"
    >
    <svg class="account-trigger__avatar" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="3.4" stroke="currentColor" stroke-width="1.7"></circle>
      <path d="M5 20c1-3.5 4-5 7-5s6 1.5 7 5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"></path>
    </svg>
    <span class="account-trigger__name">{{ user.name }}</span>
    <svg class="account-trigger__chevron" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"></path>
    </svg>
  </button>

  @if (isOpen$ | async) {
    <div
      class="account-menu"
      role="menu"
      tabindex="-1"
      (clickOutside)="onClose()"
      (keydown.escape)="onEscape()"
      >
      <span class="account-menu__caret" aria-hidden="true"></span>

      <div class="account-menu__header">
        <span class="account-menu__name">{{ user.name }}</span>
        @if (user.email) {
          <span class="account-menu__meta">{{ user.email }}</span>
        }
        @if (theme$ | async; as theme) {
          <div class="account-menu__theme" role="group" aria-label="Color theme">
            <button
              type="button"
              class="account-menu__theme-option"
              [class.account-menu__theme-option--active]="theme === 'light'"
              [attr.aria-pressed]="theme === 'light'"
              (click)="onSetTheme('light')"
              >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="1.7"></circle>
                <path d="M12 2.8v2M12 19.2v2M2.8 12h2M19.2 12h2M5.5 5.5l1.4 1.4M17.1 17.1l1.4 1.4M18.5 5.5l-1.4 1.4M6.9 17.1l-1.4 1.4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"></path>
              </svg>
              Light
            </button>
            <button
              type="button"
              class="account-menu__theme-option"
              [class.account-menu__theme-option--active]="theme === 'dark'"
              [attr.aria-pressed]="theme === 'dark'"
              (click)="onSetTheme('dark')"
              >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M20.2 13.6A8 8 0 0110.4 3.8 8 8 0 1020.2 13.6z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"></path>
              </svg>
              Dark
            </button>
          </div>
        }
      </div>

      <div class="account-menu__list" role="none">
        <button
          type="button"
          class="account-menu__item"
          role="menuitem"
          (click)="onNavigate(['/profile'])"
          >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="8" r="3.2" stroke="currentColor" stroke-width="1.7"></circle>
            <path d="M5.5 19.5c.8-3 3.2-4.5 6.5-4.5s5.7 1.5 6.5 4.5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"></path>
          </svg>
          My Profile
        </button>

        @if (canViewMyOrganization$ | async) {
          <button
            type="button"
            class="account-menu__item"
            role="menuitem"
            (click)="onNavigate(['/profile', 'organization'])"
            >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="6" y="3.5" width="12" height="16.5" rx="1.5" stroke="currentColor" stroke-width="1.7"></rect>
              <path d="M9 7.5h1.5M13.5 7.5H15M9 11h1.5M13.5 11H15M10.5 20v-4h3v4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"></path>
            </svg>
            My Organization
          </button>
        }

        @if (canViewMyOrganization$ | async) {
          <button
            type="button"
            class="account-menu__item"
            role="menuitem"
            (click)="onNavigate(['/profile', 'installation-pricing'])"
            >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 19h14" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"></path>
              <path d="M7 19V6.5A1.5 1.5 0 0 1 8.5 5h7A1.5 1.5 0 0 1 17 6.5V19" stroke="currentColor"
                stroke-width="1.7" stroke-linejoin="round"></path>
              <path d="M9.5 9h5M9.5 12.5h5M9.5 16h2" stroke="currentColor" stroke-width="1.6"
                stroke-linecap="round"></path>
            </svg>
            Installation Pricing
          </button>
        }

        @if (canViewAdminQuoteTemplates$ | async) {
          <button
            type="button"
            class="account-menu__item"
            role="menuitem"
            (click)="onNavigate(['/profile', 'report-templates'])"
            >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 4.5h14v15H5z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"></path>
              <path d="M8 8h8M8 11.5h8M8 15h4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"></path>
            </svg>
            Report Templates
          </button>
        }

        @if (canViewEvents$ | async) {
          @if (eventsRoute$ | async; as eventsRoute) {
            <button
              type="button"
              class="account-menu__item"
              role="menuitem"
              (click)="onNavigate(eventsRoute)"
              >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" stroke-width="1.7"></rect>
                <path d="M4 9.5h16M8 3.5v3M16 3.5v3" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"></path>
              </svg>
              Events
            </button>
          }
        }

        @if (isSuperAdmin$ | async) {
          <button
            type="button"
            class="account-menu__item"
            role="menuitem"
            (click)="onNavigate(['/admin', 'part-numbers'])"
            >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M4 12.6V5.5A1.5 1.5 0 015.5 4h7.1l7.4 7.4a1.5 1.5 0 010 2.12l-5.48 5.48a1.5 1.5 0 01-2.12 0L4 12.6z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"></path>
              <circle cx="8.6" cy="8.6" r="1.3" fill="currentColor"></circle>
            </svg>
            Part Numbers
          </button>
        }
      </div>

      <div class="account-menu__divider" role="none"></div>

      <button
        type="button"
        class="account-menu__item account-menu__item--danger"
        role="menuitem"
        (click)="onSignOut()"
        >
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M9.5 4H5.5A1.5 1.5 0 004 5.5v13A1.5 1.5 0 005.5 20h4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"></path>
          <path d="M15.5 8l4 4-4 4M19.5 12H9.5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
        Sign Out
      </button>
    </div>
  }
}
